import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    FaTachometerAlt,
    FaUsers,
    FaCalendarCheck,
    FaClipboardList,
    FaBell,
    FaChartBar,
    FaBars,
    FaTimes,
    FaSitemap,
    FaSignOutAlt,
    FaUserCircle,
    FaCog,
    FaFileAlt
} from 'react-icons/fa';
import './Sidebar.css';

const Sidebar = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const userRole = user?.role || 'employee';

    useEffect(() => {
        const handleToggle = () => setMobileOpen(prev => !prev);
        const handleClose = () => setMobileOpen(false);
        window.addEventListener('toggle-mobile-sidebar', handleToggle);
        window.addEventListener('close-mobile-sidebar', handleClose);
        return () => {
            window.removeEventListener('toggle-mobile-sidebar', handleToggle);
            window.removeEventListener('close-mobile-sidebar', handleClose);
        };
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed', error);
        }
    };

    const APPROVER_ROLES = ['admin', 'hr', 'manager', 'dept_head', 'production_head', 'director'];
    const ALL_ROLES = [...APPROVER_ROLES, 'employee'];

    const menuItems = [
        {
            path: '/dashboard',
            icon: <FaTachometerAlt />,
            label: 'HRM Dashboard',
            roles: ALL_ROLES
        },
        {
            path: '/my-profile',
            icon: <FaUserCircle />,
            label: 'My Profile',
            roles: ALL_ROLES
        },
        {
            path: '/employees',
            icon: <FaUsers />,
            label: 'Employee',
            roles: APPROVER_ROLES
        },
        {
            path: '/departments',
            icon: <FaSitemap />,
            label: 'Departments',
            roles: APPROVER_ROLES
        },
        {
            path: '/attendance',
            icon: <FaCalendarCheck />,
            label: 'Attendance',
            roles: ALL_ROLES
        },
        {
            path: '/leaves',
            icon: <FaClipboardList />,
            label: 'Leave Management',
            roles: ALL_ROLES
        },
        {
            path: '/wrms/admin',
            icon: <FaCog />,
            label: 'HR Control Panel',
            roles: ['hr']
        },
        {
            path: '/wrms/reports',
            icon: <FaFileAlt />,
            label: 'Leave Reports',
            roles: APPROVER_ROLES
        },
        {
            path: '/reminders',
            icon: <FaBell />,
            label: 'Reminder',
            roles: ALL_ROLES
        },
        {
            path: '/reports',
            icon: <FaChartBar />,
            label: 'HRM Reports',
            roles: APPROVER_ROLES
        }
    ];

    // Filter menu items based on user role
    const filteredMenuItems = menuItems.filter(item => item.roles.includes(userRole));

    return (
        <>
            {mobileOpen && (
                <div
                    className="sidebar-backdrop d-md-none"
                    onClick={() => setMobileOpen(false)}
                />
            )}
            <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'show' : ''}`}>
                <div className="sidebar-header">
                    <div className="brand-logo" onClick={() => { setMobileOpen(false); navigate('/dashboard'); }} style={{ cursor: 'pointer' }}>
                        {!collapsed ? (
                            <img
                                src={`${process.env.PUBLIC_URL || ''}/DIGILOGO.jpg`}
                                alt="Digi Sahyadri Logo"
                                className="sidebar-logo"
                            />
                        ) : (
                            <img
                                src={`${process.env.PUBLIC_URL || ''}/DIGILOGO.jpg`}
                                alt="Logo"
                                className="sidebar-logo collapsed-logo"
                            />
                        )}
                    </div>
                    {/* Desktop Collapse Toggle */}
                    <button
                        className="toggle-btn d-none d-md-block"
                        onClick={() => setCollapsed(!collapsed)}
                        title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                    >
                        {collapsed ? <FaBars /> : <FaTimes />}
                    </button>
                    {/* Mobile Close Button */}
                    <button
                        className="toggle-btn d-md-none"
                        onClick={() => setMobileOpen(false)}
                        title="Close Menu"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className="sidebar-menu">
                    {filteredMenuItems.map((item, index) => (
                        <NavLink
                            key={index}
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) =>
                                `nav-link ${isActive ? 'active' : ''}`
                            }
                            title={collapsed ? item.label : ''}
                        >
                            <span className="icon">{item.icon}</span>
                            {!collapsed && <span className="label">{item.label}</span>}
                        </NavLink>
                    ))}
                </div>

                <div className="sidebar-footer">
                    <div
                        className="nav-link"
                        onClick={() => { setMobileOpen(false); handleLogout(); }}
                        title={collapsed ? 'Logout' : ''}
                    >
                        <span className="icon"><FaSignOutAlt /></span>
                        {!collapsed && <span className="label">Logout</span>}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
